import { Card, Text, Group, Badge, SimpleGrid, Stack, Title, Divider } from '@mantine/core';
import { plants, MONTH_NAMES, LAST_FROST_WEEK, FIRST_FROST_WEEK } from '../data/plants';

const MONTH_WEEK_STARTS = [1, 5, 9, 14, 18, 23, 27, 31, 36, 40, 44, 49];
const MONTH_WEEK_ENDS = [4, 8, 13, 17, 22, 26, 30, 35, 39, 43, 48, 52];

interface MonthAction {
  plant: string;
  action: string;
  color: string;
}

function getMonthActions(monthIdx: number, isSelected: (name: string) => boolean): MonthAction[] {
  const ws = MONTH_WEEK_STARTS[monthIdx];
  const we = MONTH_WEEK_ENDS[monthIdx];
  const actions: MonthAction[] = [];

  for (const plant of plants) {
    if (!isSelected(plant.name)) continue;
    if (plant.indoorStart) {
      const [s, e] = plant.indoorStart;
      if (s <= we && e >= ws) {
        actions.push({ plant: plant.name, action: 'Start indoors', color: 'violet' });
      }
    }
    if (plant.transplant) {
      const [s, e] = plant.transplant;
      if (s <= we && e >= ws) {
        actions.push({ plant: plant.name, action: 'Transplant', color: 'blue' });
      }
    }
    if (plant.directSow) {
      const [s, e] = plant.directSow;
      if (s <= we && e >= ws) {
        actions.push({ plant: plant.name, action: 'Direct sow', color: 'green' });
      }
    }
    if (plant.harvestStart <= we && plant.harvestEnd >= ws) {
      actions.push({ plant: plant.name, action: 'Harvest', color: 'orange' });
    }
  }

  return actions;
}

function MonthCard({ monthIdx, isSelected }: { monthIdx: number; isSelected: (name: string) => boolean }) {
  const actions = getMonthActions(monthIdx, isSelected);
  const ws = MONTH_WEEK_STARTS[monthIdx];
  const we = MONTH_WEEK_ENDS[monthIdx];

  const hasLastFrost = LAST_FROST_WEEK >= ws && LAST_FROST_WEEK <= we;
  const hasFirstFrost = FIRST_FROST_WEEK >= ws && FIRST_FROST_WEEK <= we;

  const grouped: Record<string, { plant: string; color: string }[]> = {};
  for (const a of actions) {
    if (!grouped[a.action]) grouped[a.action] = [];
    grouped[a.action].push({ plant: a.plant, color: a.color });
  }

  const actionOrder = ['Start indoors', 'Transplant', 'Direct sow', 'Harvest'];

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Title order={5}>{MONTH_NAMES[monthIdx]}</Title>
        {hasLastFrost && (
          <Badge color="red" variant="light" size="sm">Last frost ~May 10</Badge>
        )}
        {hasFirstFrost && (
          <Badge color="red" variant="light" size="sm">First frost ~Oct 5</Badge>
        )}
      </Group>
      <Divider mb="xs" />

      {actions.length === 0 ? (
        <Text size="sm" c="dimmed">Quiet month — plan ahead!</Text>
      ) : (
        <Stack gap={8}>
          {actionOrder.map((actionName) => {
            const items = grouped[actionName];
            if (!items) return null;
            return (
              <div key={actionName}>
                <Text size="xs" fw={600} c="dimmed" mb={2}>{actionName}</Text>
                <Group gap={4}>
                  {items.map(({ plant, color }) => (
                    <Badge key={plant} size="xs" variant="light" color={color}>
                      {plant}
                    </Badge>
                  ))}
                </Group>
              </div>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}

interface CalendarViewProps {
  isSelected: (name: string) => boolean;
}

export function CalendarView({ isSelected }: CalendarViewProps) {
  const currentMonth = new Date().getMonth();

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Month-by-month view of what to do in the garden. Mapleton, UT — Zone 6b.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
        {MONTH_NAMES.map((_, i) => (
          <div key={i} style={i === currentMonth ? { outline: '2px solid var(--mantine-color-blue-5)', borderRadius: 8 } : undefined}>
            <MonthCard monthIdx={i} isSelected={isSelected} />
          </div>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
